import React, { useState } from 'react';
import styled from 'styled-components';
import DOMPurify from 'dompurify';
import { MessageBubble } from '../molecules/message-buble';
import ChatBotIcon from '../../Images/chat-bot-icon.svg';
import SystemIcon from '../../Images/system-icon.svg';
import UserChatIcon from '../../Images/user-chat-icon.svg';
import ExpertChatIcon from '../../Images/expert-icon-in-conversation.svg';
import PDFIcon from '../../Images/pdf-icon.svg';
import FileIcon from '../../Images/file-icon.svg';
import XLSXIcon from '../../Images/xlsx-icon.svg';
import JSONIcon from '../../Images/json-icon.svg';
import MDIcon from '../../Images/md-icon.svg';
import PPTXIcon from '../../Images/pptx-icon.svg';
import ExcelIcon from '../../Images/excel-icon.svg';
import WordIcon from '../../Images/word-icon.svg';
import ImageIcon from '../../Images/image-icon.svg';
import XMLIcon from '../../Images/xml-icon.svg';
import { consts } from '../../helpers/consts';
import DocumentRequestMessage from './DocumentRequestMessage';
import { LuBriefcaseBusiness, LuInfo, LuPaperclip, LuPenLine, LuSparkles, LuUserRound, LuVideo } from 'react-icons/lu';

// The widget engine's control markers are stored with the assistant's reply ("CONVERSATION:[DONE]",
// "LANGUAGE:[en]", progress/classification tags). The widget reads them; a client must never see
// them — they showed up at the end of AI intakes in the conversation view (2026-10-20).
const stripEngineMarkers = (text) => {
  if (typeof text !== 'string') return text;
  // Both forms of each marker: the model sometimes drops the brackets ("LANGUAGE:en",
  // "CONVERSATION:DONE" — a YK widget conversation on 2026-10-20 showed "LANGUAGE:en").
  // Upper-case and code-shaped only, so ordinary words ("our conversation: …",
  // "Language: English") are never eaten.
  return text
    .replace(/\bCONVERSATION:\s*\[?\s*DONE\s*\]?/g, '')
    .replace(/\bCONVERSATION:(?=\s*(?:<|$|LANGUAGE\b))/gm, '') // dangling, its [DONE] stripped upstream
    .replace(/\[DONE\]/gi, '')
    .replace(/(?:<strong>\s*)?\bLANGUAGE:\s*(?:<\/strong>\s*)?(?:\[\s*[a-z]{2}(?:-[a-z]{2,4})?\s*\]|[a-z]{2}(?:-[a-z]{2,4})?(?![a-z]))/gi, '')
    .replace(/\[PROGRESS:\s*\d+\s*\/\s*\d+\]/gi, '')
    .replace(/\[CLASSIFICATION:[^\]]*\]/gi, '')
    .replace(/<p>\s*<\/p>/gi, '')
    .trim();
};

// Configure DOMPurify to allow links
DOMPurify.setConfig({
  ALLOWED_TAGS: ['a', 'b', 'br', 'strong', 'i', 'em', 'mark', 'small', 'del', 'ins', 'sub', 'sup'],
  ALLOWED_ATTR: ['href', 'target', 'rel', 'style', 'class']
});

// Styled Components
const MessageContainer = styled.div`
  display: flex;
  justify-content: ${(props) => (props.type === 'user' ? 'flex-end' : 'flex-start')};
  align-items: flex-start;
  margin-bottom: 10px;
`;

// Sender avatars: rounded tiles with Lucide icons, the icon set of imavenai.com.
// Call sites still pass the old image (src) and/or the message type; both map to a tile.
const AVATAR_BY_SRC = {
  [UserChatIcon]: 'user',
  [ChatBotIcon]: 'gpt',
  [SystemIcon]: 'system',
  [ExpertChatIcon]: 'expert',
};
const AVATARS = {
  user: { Glyph: LuUserRound, bg: 'linear-gradient(135deg, #2c58e9, #6b61e5)', color: '#ffffff', ring: 'transparent' },
  expert: { Glyph: LuBriefcaseBusiness, bg: '#eaf1ff', color: '#3359c7', ring: '#d6e2fb' },
  gpt: { Glyph: LuSparkles, bg: 'linear-gradient(145deg, #3977f4, #735ff0)', color: '#ffffff', ring: 'transparent' },
  system: { Glyph: LuInfo, bg: '#f1f5fb', color: '#65748d', ring: '#e0e8f5' },
  'video-call': { Glyph: LuVideo, bg: '#e9f8ff', color: '#137ca9', ring: '#cdeefc' },
  'widget-upload': { Glyph: LuPaperclip, bg: '#e9f8ff', color: '#137ca9', ring: '#cdeefc' },
  whiteboard: { Glyph: LuPenLine, bg: '#f0ecff', color: '#7955cd', ring: '#e1dcff' },
};

const AvatarTile = styled.span`
  flex: 0 0 auto;
  display: grid;
  place-items: center;
  width: 32px;
  height: 32px;
  border-radius: 10px;
  background: ${(props) => props.$bg};
  color: ${(props) => props.$color};
  border: 1px solid ${(props) => props.$ring};
  box-shadow: 0 4px 10px rgba(52, 77, 151, 0.08);
  margin-right: ${(props) => (props.$side === 'right' ? '0px' : '10px')};
  margin-left: ${(props) => (props.$side === 'right' ? '10px' : '0px')};

  svg {
    width: 17px;
    height: 17px;
  }

  @media (max-width: 768px) {
    width: 26px;
    height: 26px;
    border-radius: 8px;

    svg {
      width: 14px;
      height: 14px;
    }
  }
`;

const Icon = ({ src, type, alt }) => {
  const kind = AVATARS[type] ? type : (AVATAR_BY_SRC[src] || 'expert');
  const { Glyph, bg, color, ring } = AVATARS[kind];
  return (
    <AvatarTile $bg={bg} $color={color} $ring={ring} $side={type === 'user' ? 'right' : 'left'} role="img" aria-label={alt}>
      <Glyph aria-hidden="true" />
    </AvatarTile>
  );
};

const FilePreview = styled.div`
  margin-top: 10px;
  display: flex;
  flex-direction: column;
  background: none;
  
  a {
    display: flex;
    align-items: center;
    text-decoration: none;
    color: #192947;
    margin-bottom: 5px;
    background: none;
    padding: 0;

    img {
      width: 32px;
      height: 32px;
      margin-right: 8px;
    }

    span {
      word-break: break-all;
      background: none;
    }

    &:hover {
      color: #192947;
      text-decoration: underline;
    }

    &:visited {
      color: #7b8aa3;
    }
  }
`;

const SenderBlock = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 70%;
  @media (max-width: 768px) {
    max-width: 85%;
  }
  @media (max-width: 480px) {
    max-width: 92%;
  }
`;

// Payment Request styled component
const PaymentRequestContainer = styled.div`
  padding: 15px;
  margin: 0;
  background-color: #f4f8f6;
  border-radius: 10px;
  max-width: 70%;
  min-width: 50%;
  box-sizing: border-box;
  margin-left: ${(props) => (props.type === 'user' ? '10px' : '0')};
  margin-right: ${(props) => (props.type === 'user' ? '0' : '10px')};
  
  @media (max-width: 768px) {
    max-width: 85%;
    min-width: 0;
  }
  
  @media (max-width: 480px) {
    max-width: 90%;
  }
`;

const PaymentRequestContent = styled.div`
  line-height: 1.4;
  font-size: 14px;
  
  .payment-request-title {
    display: flex;
    align-items: center;
    font-size: 15px;
    font-weight: bold;
    color: #2c64da;
    margin-bottom: 10px;
  }
  
  .payment-request-title:before {
    content: '💰';
    margin-right: 8px;
    font-size: 15px;
  }
  
  .payment-details {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }
  
  .payment-row {
    display: flex;
    align-items: center;
  }
  
  .payment-label {
    font-weight: bold;
    min-width: 80px;
    color: #192947;
    font-size: 14px;
  }
  
  .payment-value {
    color: #0f8f63;
    font-size: 14px;
    font-weight: bold;
    margin-left: 0;
  }
  
  .payment-description {
    background-color: #f5f8fd;
    border-radius: 5px;
    padding: 8px 10px;
    margin: 3px 0 6px;
    color: #192947;
    font-size: 13px;
    min-height: 18px;
  }
  
  .button-container {
    display: inline-block;
    margin-top: 4px;
  }
  
  .payment-button {
    display: inline-block;
    background-color: #335cf4;
    color: #ffffff !important;
    padding: 8px 16px;
    border-radius: 4px;
    text-decoration: none !important;
    font-weight: bold;
    font-size: 13px;
    transition: background-color 0.2s;
    width: auto;
    text-align: center;
    
    &:hover {
      background-color: #2d5bed;
      color: #ffffff !important;
    }
  }
`;

// Payment confirmation popup components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(23, 36, 62, 0.49);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const PaymentConfirmationModal = styled.div`
  background: linear-gradient(180deg, #f9fbff 0%, #f5f8fd 100%);
  border-radius: 8px;
  width: 90%;
  max-width: 500px;
  overflow: hidden;
  box-shadow: 0 5px 15px rgba(39, 68, 131, 0.14);
`;

const ModalHeader = styled.div`
  padding: 15px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #dce5f4;
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 18px;
  color: #192947;
  font-weight: 500;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #65748d;
  font-size: 24px;
  cursor: pointer;
  padding: 0;
  line-height: 1;
  &:hover {
    color: #192947;
  }
`;

const ModalBody = styled.div`
  padding: 20px 20px 15px;
`;

const WarningHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 16px;
  font-size: 18px;
  font-weight: bold;
  color: #192947;
`;

const WarningIcon = styled.span`
  color: #FFB52E;
  font-size: 24px;
  margin-right: 10px;
`;

const WarningText = styled.p`
  color: #192947;
  margin: 0 0 16px 0;
  line-height: 1.5;
`;

const WarningList = styled.ul`
  margin: 0 0 16px 0;
  padding-left: 20px;
  
  li {
    margin-bottom: 12px;
    color: #192947;
    line-height: 1.5;
  }
`;

const ModalFooter = styled.div`
  padding: 15px 20px;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  border-top: 1px solid #dce5f4;
`;

const CancelButton = styled.button`
  background-color: #e6ecf6;
  color: #192947;
  border: none;
  border-radius: 4px;
  padding: 10px 20px;
  cursor: pointer;
  font-size: 14px;
  font-weight: bold;
  
  &:hover {
    background-color: #edf2ff;
  }
`;

const ProceedButton = styled.button`
  background-color: #335cf4;
  color: #ffffff;
  border: none;
  border-radius: 4px;
  padding: 10px 20px;
  cursor: pointer;
  font-size: 14px;
  font-weight: bold;
  
  &:hover {
    background-color: #2d5bed;
  }
`;

const Timestamp = styled.div`
  font-size: 10px;
  color: #7b8aa3;
  margin-top: 5px;
  
  @media (max-width: 480px) {
    font-size: 9px;
    text-align: right;
  }
`;

// Signature Request styled components
const SignatureRequestContainer = styled.div`
  padding: 16px;
  background: linear-gradient(135deg, rgba(224, 85, 155, 0.15) 0%, rgba(200, 54, 127, 0.2) 100%);
  border-radius: 12px;
  max-width: 320px;
  min-width: 260px;
  margin-left: ${(props) => (props.type === 'user' ? '10px' : '0')};
  margin-right: ${(props) => (props.type === 'user' ? '0' : '10px')};
  border: 1px solid rgba(224, 85, 155, 0.4);
  box-shadow: 0 4px 12px rgba(224, 85, 155, 0.15);
`;

const SignatureHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  font-size: 13px;
  color: #455776;
`;

const SignaturePurpose = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #c8367f;
  margin-bottom: 8px;
`;

const SignatureDescription = styled.div`
  font-size: 13px;
  color: #33445e;
  margin-bottom: 12px;
  line-height: 1.4;
`;

const SignatureButton = styled.button`
  width: 100%;
  padding: 12px 16px;
  background: linear-gradient(135deg, #e0559b 0%, #c8367f 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(224, 85, 155, 0.4);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const SignatureStatus = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  padding: 6px 12px;
  border-radius: 16px;
  margin-top: 8px;
  background: ${props => {
    switch (props.status) {
      case 'signed': return 'rgba(18, 165, 113, 0.2)';
      case 'declined': return 'rgba(229, 72, 77, 0.2)';
      case 'expired': return '#65748d';
      default: return 'transparent';
    }
  }};
  color: ${props => {
    switch (props.status) {
      case 'signed': return '#0f8f63';
      case 'declined': return '#e5484d';
      case 'expired': return '#7b8aa3';
      default: return 'inherit';
    }
  }};
  border: 1px solid ${props => {
    switch (props.status) {
      case 'signed': return 'rgba(18, 165, 113, 0.4)';
      case 'declined': return 'rgba(229, 72, 77, 0.4)';
      case 'expired': return '#65748d';
      default: return 'transparent';
    }
  }};
`;

// Service Questionnaire styled components
const ServiceQuestionnaireContainer = styled.div`
  padding: 16px 20px;
  background: ${props => {
    switch (props.serviceType) {
      case 'real_estate':
        return 'linear-gradient(135deg, rgba(51, 92, 244, 0.2) 0%, rgba(45, 91, 237, 0.25) 100%)';
      case 'employment':
        return 'linear-gradient(135deg, rgba(239, 155, 97, 0.2) 0%, rgba(224, 123, 60, 0.25) 100%)';
      case 'english_academy':
        return 'linear-gradient(135deg, rgba(136, 93, 232, 0.2) 0%, rgba(121, 85, 205, 0.25) 100%)';
      default:
        return 'linear-gradient(135deg, rgba(239, 155, 97, 0.2) 0%, rgba(224, 123, 60, 0.25) 100%)';
    }
  }};
  border-radius: 14px;
  max-width: 320px;
  min-width: 260px;
  margin-right: 10px;
  border: 1px solid ${props => {
    switch (props.serviceType) {
      case 'real_estate': return 'rgba(51, 92, 244, 0.4)';
      case 'employment': return 'rgba(239, 155, 97, 0.4)';
      case 'english_academy': return 'rgba(136, 93, 232, 0.4)';
      default: return 'rgba(239, 155, 97, 0.4)';
    }
  }};
  box-shadow: 0 4px 16px ${props => {
    switch (props.serviceType) {
      case 'real_estate': return 'rgba(51, 92, 244, 0.2)';
      case 'employment': return 'rgba(239, 155, 97, 0.2)';
      case 'english_academy': return 'rgba(136, 93, 232, 0.2)';
      default: return 'rgba(239, 155, 97, 0.2)';
    }
  }};
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 24px ${props => {
      switch (props.serviceType) {
        case 'real_estate': return 'rgba(51, 92, 244, 0.3)';
        case 'employment': return 'rgba(239, 155, 97, 0.3)';
        case 'english_academy': return 'rgba(136, 93, 232, 0.3)';
        default: return 'rgba(239, 155, 97, 0.3)';
      }
    }};
  }
`;

const ServiceQuestionnaireIcon = styled.div`
  font-size: 32px;
  margin-bottom: 10px;
`;

const ServiceQuestionnaireTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: ${props => {
    switch (props.serviceType) {
      case 'real_estate': return '#2c64da';
      case 'employment': return '#ef9b61';
      case 'english_academy': return '#885de8';
      default: return '#ef9b61';
    }
  }};
  margin-bottom: 8px;
`;

const ServiceQuestionnaireDescription = styled.div`
  font-size: 13px;
  color: #33445e;
  line-height: 1.5;
  margin-bottom: 14px;
`;

const ServiceQuestionnaireButton = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: ${props => {
    switch (props.serviceType) {
      case 'real_estate': return 'linear-gradient(135deg, #335cf4 0%, #2d5bed 100%)';
      case 'employment': return 'linear-gradient(135deg, #ef9b61 0%, #e07b3c 100%)';
      case 'english_academy': return 'linear-gradient(135deg, #885de8 0%, #7955cd 100%)';
      default: return 'linear-gradient(135deg, #ef9b61 0%, #e07b3c 100%)';
    }
  }};
  color: #192947;
  padding: 10px 18px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 14px;
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(39, 68, 131, 0.088);
  }
`;

const ChatMessage = ({ message, type, timestamp, senderName, onSignatureClick, onDocumentAttach }) => {
  const { content, fileName, fileType } = message;

  // Detect an adviser document-request message (JSON with requestId + items)
  const parseDocumentRequest = (contentStr) => {
    if (!contentStr || typeof contentStr !== 'string') return null;
    try {
      const parsed = JSON.parse(contentStr);
      if (parsed && parsed.requestId && Array.isArray(parsed.items)) return parsed;
    } catch { /* not a document request */ }
    return null;
  };

  // Remove Markdown code fences (e.g., ```html ... ``` or ``` ... ```)
  const stripCodeFences = (text) => {
    if (typeof text !== 'string') return text;
    const trimmed = text.trim();
    // Full-block fenced code with optional language
    const blockMatch = trimmed.match(/^```[a-zA-Z0-9+\-]*\s*\n([\s\S]*?)\n```\s*$/);
    if (blockMatch) {
      return blockMatch[1].trim();
    }
    // Single-line fenced code
    const inlineMatch = trimmed.match(/^```[a-zA-Z0-9+\-]*\s*([\s\S]*?)\s*```\s*$/);
    if (inlineMatch) {
      return inlineMatch[1].trim();
    }
    return text;
  };
  const [showPaymentConfirmation, setShowPaymentConfirmation] = useState(false);
  const [pendingPaymentUrl, setPendingPaymentUrl] = useState(null);

  // Check if this is a payment request message 
  // Looking for "PAYMENT REQUEST" in any casing
  const paymentRequestRegex = /payment\s+request/i;
  const isPaymentRequest = type === 'expert' && 
                          content && 
                          paymentRequestRegex.test(content);

  // Payment confirmation popup component
  const PaymentConfirmation = ({ url, onCancel, onProceed }) => {
    return (
      <ModalOverlay>
        <PaymentConfirmationModal>
          <ModalHeader>
            <ModalTitle>External Payment Confirmation</ModalTitle>
            <CloseButton onClick={onCancel}>×</CloseButton>
          </ModalHeader>
          <ModalBody>
            <WarningHeader>
              <WarningIcon>⚠️</WarningIcon>
              External Payment Warning
            </WarningHeader>
            <WarningText>
              You are about to be redirected to an external payment system. Please note:
            </WarningText>
            <WarningList>
              <li>iMaven Ai is not responsible for transactions conducted on external platforms.</li>
              <li>We cannot guarantee the security of external payment sites.</li>
              <li>Please ensure you trust this payment link before proceeding.</li>
              <li>Your data protection on external sites is subject to their privacy policies.</li>
            </WarningList>
            <WarningText>
              If you have any concerns, please contact your advisor for alternative payment methods.
            </WarningText>
          </ModalBody>
          <ModalFooter>
            <CancelButton onClick={onCancel}>Cancel</CancelButton>
            <ProceedButton onClick={() => onProceed(url)}>I Understand, Proceed</ProceedButton>
          </ModalFooter>
        </PaymentConfirmationModal>
      </ModalOverlay>
    );
  };

  let iconUrl;
  switch (type) {
    case 'user':
      iconUrl = UserChatIcon;
      break;
    case 'gpt':
      iconUrl = ChatBotIcon;
      break;
    case 'system':
      iconUrl = SystemIcon;
      break;
    case 'expert':
      iconUrl = ExpertChatIcon;
      break;
    default:
      iconUrl = ChatBotIcon;
  }

  // Handle document request (adviser asked the client to attach files)
  const documentRequest = parseDocumentRequest(content);
  if (documentRequest) {
    return (
      <DocumentRequestMessage
        data={documentRequest}
        timestamp={timestamp}
        onAttach={(requestId, item, file) =>
          onDocumentAttach
            ? onDocumentAttach(requestId, item, file)
            : Promise.reject(new Error('Attach handler unavailable'))
        }
      />
    );
  }

  // Handle file content
  if (fileName && fileType && content) {
    const fileUrl = content;
    const decodedFileName = decodeURIComponent(fileName.split('/').pop());

    let fileIcon;
    if (fileType.startsWith(consts.fileTypes.image)) {
      fileIcon = ImageIcon;
    } else if (fileType === consts.fileTypes.pdf) {
      fileIcon = PDFIcon;
    } else if (fileType === consts.fileTypes.xlsx) {
      fileIcon = XLSXIcon;
    } else if (fileType === consts.fileTypes.json) {
      fileIcon = JSONIcon;
    } else if (fileType === consts.fileTypes.markdown) {
      fileIcon = MDIcon;
    } else if (fileType === consts.fileTypes.pptx) {
      fileIcon = PPTXIcon;
    } else if (fileType === consts.fileTypes.excel) {
      fileIcon = ExcelIcon;
    } else if (fileType === consts.fileTypes.word) {
      fileIcon = WordIcon;
    } else if (fileType === consts.fileTypes.xml) {
      fileIcon = XMLIcon;
    } else {
      fileIcon = FileIcon;
    }

    return (
      <MessageContainer type={type}>
        {type !== 'user' && <Icon src={iconUrl} alt={`${type} icon`} type={type} />}
        <SenderBlock>
          <MessageBubble
            type={type}
            senderName={senderName}
            content={
              <FilePreview>
                <a href={fileUrl} download={decodedFileName}>
                  <img src={fileIcon} alt={`${fileType} Icon`} />
                  <span>{decodedFileName}</span>
                </a>
              </FilePreview>
            }
            timestamp={timestamp}
          />
        </SenderBlock>
        {type === 'user' && <Icon src={iconUrl} alt={`${type} icon`} type={type} />}
      </MessageContainer>
    );
  }

  // Handle video call content
  if (type === 'video-call' && content && content.startsWith('/video-call/')) {
    return (
      <MessageContainer type={type}>
        {type !== 'user' && <Icon src={iconUrl} alt={`${type} icon`} type={type} />}
        <MessageBubble
          type={type}
          content="Click here to join video call"
          timestamp={timestamp}
          onClick={() => {
            const videoCallUrl = window.location.origin + content;
            window.open(videoCallUrl, '_blank', 'noopener,noreferrer');
          }}
        />
        {type === 'user' && <Icon src={iconUrl} alt={`${type} icon`} type={type} />}
      </MessageContainer>
    );
  }

  // Helper function to detect signature request content by structure
  const isSignatureRequestContent = (contentStr) => {
    if (!contentStr || typeof contentStr !== 'string') return false;
    try {
      const parsed = JSON.parse(contentStr);
      return parsed && parsed.requestCode && parsed.purpose && typeof parsed.status === 'string';
    } catch {
      return false;
    }
  };

  // Helper function to detect service questionnaire content by structure
  const isServiceQuestionnaireContent = (contentStr) => {
    if (!contentStr || typeof contentStr !== 'string') return false;
    try {
      const parsed = JSON.parse(contentStr);
      return parsed && parsed.serviceType && parsed.sessionId && (parsed.serviceName || parsed.title);
    } catch {
      return false;
    }
  };

  // Helper function to detect AI analysis complete content
  const isAiAnalysisContent = (contentStr) => {
    if (!contentStr || typeof contentStr !== 'string') return false;
    try {
      const parsed = JSON.parse(contentStr);
      return parsed && parsed.type === 'ai_analysis_complete' && parsed.threadId;
    } catch {
      return false;
    }
  };

  // Handle AI analysis complete content
  if (isAiAnalysisContent(content)) {
    let analysisInfo;
    try {
      analysisInfo = JSON.parse(content);
    } catch {
      analysisInfo = { threadId: '', title: 'AI Case Analysis' };
    }

    const handleViewAnalysis = () => {
      const analysisUrl = `${window.location.origin}/ai-analysis/${analysisInfo.threadId}`;
      window.open(analysisUrl, '_blank', 'noopener,noreferrer');
    };

    return (
      <div style={{
        padding: '16px 20px',
        background: 'linear-gradient(135deg, rgba(136, 93, 232, 0.15) 0%, rgba(136, 93, 232, 0.2) 100%)',
        borderRadius: '14px',
        maxWidth: '340px',
        minWidth: '280px',
        marginRight: '10px',
        marginBottom: '12px',
        border: '1px solid rgba(136, 93, 232, 0.3)',
        boxShadow: '0 4px 16px rgba(136, 93, 232, 0.15)',
        cursor: 'pointer',
        transition: 'all 0.2s ease'
      }}
        onClick={handleViewAnalysis}
        onMouseOver={(e) => {
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = '0 6px 24px rgba(136, 93, 232, 0.25)';
        }}
        onMouseOut={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 4px 16px rgba(136, 93, 232, 0.15)';
        }}
      >
        <div style={{ marginBottom: '10px' }}>
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#7955cd" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2a4 4 0 0 1 4 4v2a4 4 0 0 1-8 0V6a4 4 0 0 1 4-4z"/><path d="M16 14H8a4 4 0 0 0-4 4v2h16v-2a4 4 0 0 0-4-4z"/><circle cx="9" cy="7" r="0.5" fill="#7955cd"/><circle cx="15" cy="7" r="0.5" fill="#7955cd"/></svg>
        </div>
        <div style={{
          fontSize: '16px', fontWeight: '600', color: '#7955cd',
          marginBottom: '6px'
        }}>
          {analysisInfo.title || 'AI Case Analysis'}
        </div>
        <div style={{
          fontSize: '13px', color: '#65748d',
          lineHeight: '1.4', marginBottom: '14px'
        }}>
          {analysisInfo.description || 'Your professional AI case analysis is ready. Click to view your personalized results.'}
        </div>
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: '6px',
          padding: '8px 16px', borderRadius: '8px',
          background: 'rgba(136, 93, 232, 0.3)',
          color: '#7955cd', fontSize: '13px', fontWeight: '600'
        }}>
          View Analysis &rarr;
        </div>
      </div>
    );
  }

  // Handle signature_request content
  if (type === 'signature_request' || isSignatureRequestContent(content)) {
    let signatureData;
    try {
      signatureData = typeof content === 'string' ? JSON.parse(content) : content;
    } catch {
      signatureData = { purpose: 'Signature Request', status: 'pending' };
    }

    const getStatusText = (status) => {
      switch (status) {
        case 'signed': return '✅ Signed successfully';
        case 'declined': return '❌ Declined';
        case 'expired': return '⏰ Request expired';
        case 'cancelled': return '🚫 Cancelled';
        default: return null;
      }
    };

    const isPending = !signatureData.status || signatureData.status === 'pending';

    return (
      <MessageContainer type="system">
        <Icon src={SystemIcon} alt="system icon" />
        <SenderBlock>
          <SignatureRequestContainer>
            <SignatureHeader>
              ✍️ Signature Request
            </SignatureHeader>
            <SignaturePurpose>{signatureData.purpose}</SignaturePurpose>
            {signatureData.description && (
              <SignatureDescription>
                {signatureData.description}
              </SignatureDescription>
            )}
            
            {isPending ? (
              <SignatureButton 
                onClick={() => onSignatureClick && onSignatureClick(signatureData)}
              >
                ✍️ Provide Signature
              </SignatureButton>
            ) : (
              <SignatureStatus status={signatureData.status}>
                {getStatusText(signatureData.status)}
              </SignatureStatus>
            )}
            
            <Timestamp>
              {new Date(timestamp).toLocaleString([], {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              })}
            </Timestamp>
          </SignatureRequestContainer>
        </SenderBlock>
      </MessageContainer>
    );
  }

  // Handle service questionnaire content
  if (type?.startsWith('service_questionnaire_') || isServiceQuestionnaireContent(content)) {
    let questionnaireData;
    try {
      questionnaireData = typeof content === 'string' ? JSON.parse(content) : content;
    } catch {
      questionnaireData = { serviceName: 'Service Questionnaire', serviceIcon: '📋', serviceType: 'employment' };
    }

    const getServiceIcon = (serviceType) => {
      switch (serviceType) {
        case 'real_estate': return '🏠';
        case 'employment': return '💼';
        case 'english_academy': return '📚';
        default: return '📋';
      }
    };

    const getServiceDescription = (serviceType) => {
      switch (serviceType) {
        case 'real_estate':
          return 'Answer questions about your housing preferences to help find the perfect home for you.';
        case 'employment':
          return 'Complete your professional profile and get matched with the best job opportunities.';
        case 'english_academy':
          return 'Assess your language needs and find the right English courses for you.';
        default:
          return 'Click to complete this questionnaire.';
      }
    };

    const handleQuestionnaireClick = () => {
      if (questionnaireData.sessionUrl) {
        window.open(questionnaireData.sessionUrl, '_blank', 'noopener,noreferrer');
      } else if (questionnaireData.sessionId) {
        const questionnaireUrl = `${window.location.origin}/service-questionnaire/${questionnaireData.sessionId}`;
        window.open(questionnaireUrl, '_blank', 'noopener,noreferrer');
      }
    };

    return (
      <MessageContainer type="expert">
        <Icon src={ExpertChatIcon} alt="expert icon" type="expert" />
        <SenderBlock>
          <ServiceQuestionnaireContainer 
            serviceType={questionnaireData.serviceType}
            onClick={handleQuestionnaireClick}
          >
            <ServiceQuestionnaireIcon>
              {getServiceIcon(questionnaireData.serviceType)}
            </ServiceQuestionnaireIcon>
            <ServiceQuestionnaireTitle serviceType={questionnaireData.serviceType}>
              {questionnaireData.serviceName || questionnaireData.title || 'Service Questionnaire'}
            </ServiceQuestionnaireTitle>
            <ServiceQuestionnaireDescription>
              {getServiceDescription(questionnaireData.serviceType)}
            </ServiceQuestionnaireDescription>
            <ServiceQuestionnaireButton serviceType={questionnaireData.serviceType}>
              <span>Start Questionnaire</span>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M5 12h14M12 5l7 7-7 7"/>
              </svg>
            </ServiceQuestionnaireButton>
            <Timestamp>
              {new Date(timestamp).toLocaleString([], {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              })}
            </Timestamp>
          </ServiceQuestionnaireContainer>
        </SenderBlock>
      </MessageContainer>
    );
  }

  // Handle whiteboard content
  if (type === 'whiteboard' || (content && content.includes('/whiteboard/'))) {
    return (
      <MessageContainer type={type}>
        {type !== 'user' && <Icon src={iconUrl} alt={`${type} icon`} type={type} />}
        <MessageBubble
          type={type}
          senderName={senderName}
          content={
            <div 
              style={{ 
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 12px',
                background: 'rgba(239, 155, 97, 0.15)',
                borderRadius: '8px',
                border: '1px solid rgba(239, 155, 97, 0.3)'
              }}
              onClick={() => {
                const whiteboardUrl = content.startsWith('/') 
                  ? window.location.origin + content 
                  : window.location.origin + '/' + content.replace(/.*?(\/whiteboard\/)/, 'whiteboard/');
                console.log('[Whiteboard] Message content:', content);
                console.log('[Whiteboard] Opening URL:', whiteboardUrl);
                window.open(whiteboardUrl, '_blank', 'noopener,noreferrer');
              }}
            >
              <span style={{ fontSize: '20px' }}>📝</span>
              <span style={{ color: '#a2611c', fontWeight: '600' }}>Join Whiteboard Session</span>
            </div>
          }
          timestamp={timestamp}
        />
        {type === 'user' && <Icon src={iconUrl} alt={`${type} icon`} type={type} />}
      </MessageContainer>
    );
  }

  // Handle payment request content
  if (isPaymentRequest) {
    // Extract description from HTML content
    let description = "";
    const cleanedContent = stripCodeFences(content || "");

    // Handle both HTML and plain text formats
    if (cleanedContent.includes("<div")) {
      try {
        // Create a temporary div to parse the HTML
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = DOMPurify.sanitize(cleanedContent);
        
        // Look for description content - using general approach
        const descriptionDivs = tempDiv.querySelectorAll('div');
        
        // Find the div after the one containing "Description:"
        for (let i = 0; i < descriptionDivs.length; i++) {
          if (descriptionDivs[i].textContent.includes('Description:')) {
            // Try to get the next div or the parent's next child
            if (i + 1 < descriptionDivs.length) {
              // Check if this is the actual description content
              const potentialDesc = descriptionDivs[i + 1].textContent.trim();
              if (potentialDesc && !potentialDesc.includes('Payment Link:')) {
                description = potentialDesc;
                break;
              }
            }
          }
        }
        
        // If we couldn't find it with that approach, try to look for specific styling
        if (!description) {
          const styledDivs = Array.from(descriptionDivs).filter(div => 
            div.getAttribute('style') && 
            div.getAttribute('style').includes('white-space: pre-wrap')
          );
          
          if (styledDivs.length > 0) {
            description = styledDivs[0].textContent.trim();
          }
        }
        
        // If nothing worked, try to get any text between description and payment link
        if (!description) {
          const text = tempDiv.textContent;
          const descIndex = text.indexOf('Description:') + 'Description:'.length;
          const paymentIndex = text.indexOf('Payment Link:');
          
          if (descIndex > 0 && paymentIndex > descIndex) {
            description = text.substring(descIndex, paymentIndex).trim();
          }
        }
      } catch (error) {
        console.error("Error extracting description from HTML:", error);
      }
    } else {
      // Handle plain text format
      const descriptionMatch = cleanedContent.match(/Description:([^]*?)(?:Payment Link:|$)/i);
      if (descriptionMatch && descriptionMatch[1]) {
        description = descriptionMatch[1].trim();
      }
    }
    
    // Clean up the description - remove any HTML tags
    if (description) {
      description = description.replace(/<[^>]*>/g, '');
      description = DOMPurify.sanitize(description, { ALLOWED_TAGS: [] });
    }
    
    console.log("Final description:", description);
    
    // Extract URL using regex
    const links = cleanedContent.match(consts.urlRegex);
    const paymentUrl = links && links.length > 0 ? links[0] : null;
    
    // Extract amount using regex, assuming amount is preceded by "Amount:" or followed by a currency symbol
    const amountMatch = content.match(consts.amountRegex);
    const amount = amountMatch ? (amountMatch[1] || amountMatch[2]) : "0";
    
    // Create structured payment request content
    const formattedContent = `
      <div class="payment-request-title">PAYMENT REQUEST</div>
      <div class="payment-details">
        <div class="payment-row">
          <div class="payment-label">Amount:</div>
          <div class="payment-value">$${amount}</div>
        </div>
        
        <div class="payment-row">
          <div class="payment-label">Description:</div>
        </div>
        <div class="payment-description">${description ? DOMPurify.sanitize(description, { ALLOWED_TAGS: [] }) : '&nbsp;'}</div>
        
        <div class="payment-row">
          <div class="payment-label">Payment Link:</div>
        </div>
        <div class="button-container">
          ${paymentUrl ? `<a href="#" data-payment-url="${paymentUrl}" class="payment-button">Click to Pay</a>` : ''}
        </div>
      </div>
    `;
    
    // Handle click on the payment button
    const handlePaymentClick = (e) => {
      const target = e.target;
      if (target.classList && target.classList.contains('payment-button')) {
        e.preventDefault();
        e.stopPropagation();
        
        // Get payment URL from data attribute
        const paymentUrl = target.getAttribute('data-payment-url');
        if (paymentUrl) {
          // Show confirmation dialog instead of opening directly
          setPendingPaymentUrl(paymentUrl);
          setShowPaymentConfirmation(true);
        }
      }
    };
    
    // Handle confirmation dialog cancel
    const handleCancel = () => {
      setShowPaymentConfirmation(false);
      setPendingPaymentUrl(null);
    };
    
    // Handle confirmation dialog proceed
    const handleProceed = (url) => {
      setShowPaymentConfirmation(false);
      setPendingPaymentUrl(null);
      
      // Open payment URL in new tab
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    };
    
    // Icon for expert
    const iconUrl = ExpertChatIcon;
    
    // Return specialized payment request layout
    return (
      <>
        <MessageContainer type={type}>
          {type !== 'user' && <Icon src={iconUrl} alt="expert icon" type={type} />}
          <SenderBlock>
            <PaymentRequestContainer type={type} onClick={handlePaymentClick}>
              <PaymentRequestContent dangerouslySetInnerHTML={{ __html: formattedContent }} />
              <Timestamp>
                {new Date(timestamp).toLocaleString([], {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </Timestamp>
            </PaymentRequestContainer>
          </SenderBlock>
          {type === 'user' && <Icon src={iconUrl} alt={`${type} icon`} type={type} />}
        </MessageContainer>
        
        {showPaymentConfirmation && (
          <PaymentConfirmation 
            url={pendingPaymentUrl}
            onCancel={handleCancel}
            onProceed={handleProceed}
          />
        )}
      </>
    );
  }

  // Handle regular message content
  const cleanedText = stripEngineMarkers(stripCodeFences(content || ''));
  const hasHtmlTags = /<\/?[a-z][\s\S]*>/i.test(cleanedText);
  const renderedContent = hasHtmlTags
    ? <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(cleanedText, {
        ALLOWED_TAGS: ['div', 'span', 'a', 'br', 'p', 'strong', 'em', 'b', 'i', 'u', 'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'table', 'thead', 'tbody', 'tr', 'th', 'td', 'hr'],
        ALLOWED_ATTR: ['style', 'href', 'target', 'class', 'id']
      }) }} />
    : cleanedText;

  return (
    <MessageContainer type={type}>
      {type !== 'user' && <Icon src={iconUrl} alt={`${type} icon`} type={type} />}
      <SenderBlock>
        <MessageBubble
          type={type}
          senderName={senderName}
          content={renderedContent}
          timestamp={timestamp}
        />
      </SenderBlock>
      {type === 'user' && <Icon src={iconUrl} alt={`${type} icon`} type={type} />}
    </MessageContainer>
  );
};

export default ChatMessage;